import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import { actors } from '@/mocks/actors';
import { news, hotKeywords } from '@/mocks/news';

export default function Home() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');

  const featured = movies[0];
  const featuredActors = useMemo(
    () => actors.filter((a) => featured.actorIds.includes(a.id)),
    [featured],
  );
  const recommendations = useMemo(
    () => movies.filter((m) => m.id !== featured.id).slice(0, 6),
    [featured],
  );
  const actorNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    actors.forEach((a) => {
      map[a.id] = a.name;
    });
    return map;
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/movies?q=${encodeURIComponent(q)}` : '/movies');
  };

  return (
    <SiteLayout>
      <title>蜜桃成熟时33D高清在线观看与剧情介绍</title>
      <meta
        name="description"
        content="蜜桃成熟时33D网站提供该电影的高清资源、剧情介绍以及演员信息，让用户方便获取详细影视内容和观影指导。"
      />
      <meta
        name="keywords"
        content="蜜桃成熟时33D,蜜桃成熟时33D电影,蜜桃成熟时33D剧情,蜜桃成熟时33D在线观看,蜜桃成熟时33D演员"
      />
      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={featured.backdrop}
            alt="蜜桃成熟时33D主视觉背景"
            title="蜜桃成熟时33D电影主视觉"
            className="h-full w-full object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/65 to-foreground-950/40"></div>

        <div className="relative mx-auto grid w-full grid-cols-1 items-center gap-10 px-4 py-16 md:px-6 md:py-24 lg:grid-cols-12">
          <div className="w-full animate-fade-up lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/40 bg-primary-500/15 px-3 py-1 text-xs font-medium text-primary-200">
              <i className="ri-fire-line"></i>
              本网站焦点影片 · {featured.year}
            </span>
            <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {featured.title}
              <span className="ml-2 text-lg font-normal text-primary-300 md:text-2xl">
                高清在线观看与剧情介绍
              </span>
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              {featured.excerpt}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-background-200">
              <span className="rounded-md bg-background-50/10 px-2.5 py-1">
                {featured.region}
              </span>
              <span className="rounded-md bg-background-50/10 px-2.5 py-1">
                {featured.duration} 分钟
              </span>
              <span className="rounded-md bg-background-50/10 px-2.5 py-1">
                导演 {featured.director}
              </span>
              {featured.genres.map((g) => (
                <span key={g} className="rounded-md bg-background-50/10 px-2.5 py-1">
                  {g}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={`/movies/detail/${featured.id}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/movies/detail/${featured.id}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/20"
              >
                查看详情
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <form
              onSubmit={handleSearch}
              className="mt-8 flex w-full max-w-xl items-center gap-2 rounded-full border border-background-50/25 bg-background-50/95 px-4 py-2"
            >
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                name="keyword"
                placeholder="搜索电影标题或演员姓名"
                className="w-full bg-transparent py-1.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          <div className="w-full animate-fade-up delay-200 lg:col-span-5">
            <div className="relative mx-auto w-full max-w-xs lg:max-w-sm">
              <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-50/20 bg-background-900">
                <img
                  src={featured.poster}
                  alt="蜜桃成熟时33D电影海报"
                  title="蜜桃成熟时33D高清海报"
                  className="h-full w-full object-top"
                />
                <Link
                  to={`/movies/detail/${featured.id}`}
                  aria-label="播放入口"
                  className="absolute inset-0 flex items-center justify-center bg-foreground-950/20 transition-colors hover:bg-foreground-950/35"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-110">
                    <i className="ri-play-fill text-3xl"></i>
                  </span>
                </Link>
              </div>
              <div className="mt-3 flex items-center justify-between rounded-lg bg-background-50/95 px-4 py-3">
                <div>
                  <p className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-star-fill text-accent-500"></i>
                    演示评分 {featured.rating.toFixed(1)}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    浏览 {featured.views.toLocaleString()} 次
                  </p>
                </div>
                <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
                  演示资源
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main two-column */}
      <section className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-14 md:px-6 lg:grid-cols-12">
        {/* Left column */}
        <div className="lg:col-span-8">
          {/* Synopsis module */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-book-open-line"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
            </div>
            <p className="mt-4 text-sm leading-7 text-foreground-700">{featured.synopsis[0]}</p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">{featured.synopsis[1]}</p>
            <Link
              to={`/movies/detail/${featured.id}`}
              className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              查看完整剧情
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Cast strip */}
          <div className="mt-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-foreground-950">主要演员</h2>
              <Link to="/actors" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                全部演员
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {featuredActors.map((a) => (
                <Link
                  key={a.id}
                  to={`/actors/detail/${a.id}`}
                  className="group flex flex-col items-center rounded-lg border border-background-200 bg-background-50 p-4 text-center transition-colors hover:border-primary-300"
                >
                  <div className="h-20 w-20 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={a.avatar}
                      alt={`${a.name}照片`}
                      title={`${a.name}演员资料`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-3 text-sm font-medium text-foreground-950">{a.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{a.roleTag}</p>
                </Link>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="mt-10">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-foreground-950">推荐影片</h2>
              <Link to="/movies" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                更多影片
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {recommendations.map((m) => (
                <MovieCard
                  key={m.id}
                  movie={m}
                  actorNames={m.actorIds.slice(0, 2).map((id) => actorNameMap[id])}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <aside className="flex flex-col gap-8 lg:col-span-4">
          {/* News */}
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-newspaper-line"></i>
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">相关资讯</h2>
            </div>
            <ul className="mt-5 space-y-4">
              {news.map((item) => (
                <li key={item.id} className="border-b border-background-200 pb-4 last:border-0 last:pb-0">
                  <Link to="/about" className="group block">
                    <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
                        {item.category}
                      </span>
                      <span>{item.date}</span>
                    </div>
                    <p className="mt-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Hot keywords */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-fire-line"></i>
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">热搜关键词</h2>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {hotKeywords.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => navigate(`/movies?q=${encodeURIComponent(k)}`)}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          {/* Site intro */}
          <div className="rounded-lg bg-primary-950 p-6 text-background-100">
            <h2 className="font-heading text-lg font-bold text-background-50">关于本站</h2>
            <p className="mt-3 text-sm leading-relaxed text-background-300">
              蜜桃成熟时33D 影视资讯站专注于为影迷提供影片资料、剧情介绍、演员信息与观影指南。
            </p>
            <p className="mt-3 text-xs leading-relaxed text-background-400">
              声明：本站为纯演示项目，不提供任何真实影片播放与下载服务，站内数据均为虚构。
            </p>
            <Link
              to="/about"
              className="mt-5 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              了解更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </section>
    </SiteLayout>
  );
}